// PreferencesContext.jsx
// Single source of truth for per-user UI preferences (theme, date format, money
// format, saved filter views). DB is authoritative (via getuserprefs.sz /
// setuserprefs.sz); localStorage is only a fast cache so theme/formats apply on
// first paint with no flash. Preferences auto-save on change; saved views are
// written explicitly by the "Save filters" toolbar (see useSavedView).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import useFetchDatav2 from "../components/useFetchDatav2";
import { DEFAULT_DATE_FORMAT, DEFAULT_MONEY, formatDate, formatMoney } from "../utils/formatters";

const CACHE_KEY = "userPrefs";
const SKIN_KEY = "skin-mode"; // shared with the App.jsx bootstrap FOUC guard

export const DEFAULT_PREFS = {
   theme: "light", // "light" | "dark"
   dateFormat: DEFAULT_DATE_FORMAT,
   money: { ...DEFAULT_MONEY },
   savedViews: { invoices: null, invoiceStatements: null },
};

// Shallow-with-known-nesting merge for our fixed pref shape.
function mergePrefs(base, over) {
   const o = over || {};
   return {
      ...base,
      ...o,
      money: { ...base.money, ...(o.money || {}) },
      savedViews: { ...base.savedViews, ...(o.savedViews || {}) },
   };
}

function readCache() {
   try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
   } catch {
      return null;
   }
}
function writeCache(prefs) {
   try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(prefs));
   } catch {
      /* ignore quota/availability errors */
   }
}

function applyTheme(theme) {
   try {
      const html = document.querySelector("html");
      if (!html) return;
      if (theme === "dark") {
         html.setAttribute("data-skin", "dark");
         localStorage.setItem(SKIN_KEY, "dark");
      } else {
         html.removeAttribute("data-skin");
         localStorage.setItem(SKIN_KEY, "light");
      }
   } catch {
      /* ignore */
   }
}

const PreferencesContext = createContext(null);

export const PreferencesProvider = ({ children }) => {
   const { authReady } = useAuth();
   const { fetchData } = useFetchDatav2();

   const [prefs, setPrefs] = useState(() => mergePrefs(DEFAULT_PREFS, readCache()));
   const [prefsReady, setPrefsReady] = useState(false);

   // Latest prefs without re-creating callbacks; keeps state updates pure.
   const prefsRef = useRef(prefs);
   useEffect(() => {
      prefsRef.current = prefs;
   }, [prefs]);

   const loadedRef = useRef(false);

   // useFetchDatav2 returns a NEW fetchData each render (not memoized). Hold the latest
   // in a ref so the load effect can depend only on [authReady] — otherwise the effect
   // re-runs every render, its cleanup cancels the in-flight load, and prefsReady never
   // gets set.
   const fetchDataRef = useRef(fetchData);
   useEffect(() => {
      fetchDataRef.current = fetchData;
   });

   // 1. Apply cached theme on mount (App.jsx already set data-skin pre-React;
   //    this keeps React/DOM consistent).
   useEffect(() => {
      applyTheme(prefsRef.current.theme);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // 2. Load from the server once authenticated. Server wins over cache.
   useEffect(() => {
      if (!authReady || loadedRef.current) return;
      loadedRef.current = true;
      let cancelled = false;

      (async () => {
         try {
            const { responseData } = await fetchDataRef.current("/getuserprefs.sz", "POST", new FormData(), "form-data", "json");
            if (cancelled) return;
            if (responseData && responseData.st === 1 && Array.isArray(responseData.payload)) {
               const map = {};
               for (const row of responseData.payload) {
                  if (row && row.up_key !== undefined) map[row.up_key] = row.up_value;
               }
               const cleaned = {
                  // savedViews are always authoritative from the server
                  savedViews: {
                     invoices: map["savedView.invoices"] ?? null,
                     invoiceStatements: map["savedView.invoiceStatements"] ?? null,
                  },
               };
               if (map.theme !== undefined) cleaned.theme = map.theme;
               if (map.dateFormat !== undefined) cleaned.dateFormat = map.dateFormat;
               if (map.money !== undefined) cleaned.money = map.money;

               const merged = mergePrefs(prefsRef.current, cleaned);
               prefsRef.current = merged;
               setPrefs(merged);
               writeCache(merged);
               applyTheme(merged.theme); // server wins; may flip if cache disagreed
            }
         } catch {
            /* keep cached prefs on failure — DB is source of truth but cache is a safe fallback */
         } finally {
            if (!cancelled) setPrefsReady(true);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [authReady]);

   // Persist a single key to the server (fire-and-forget; optimistic UI already applied).
   const persistKey = useCallback(
      async (serverKey, value) => {
         try {
            const body = new FormData();
            body.append("_key", serverKey);
            body.append("_value", JSON.stringify(value));
            await fetchData("/setuserprefs.sz", "POST", body, "form-data", "json");
         } catch {
            /* next load reconciles */
         }
      },
      [fetchData]
   );

   // Optimistic commit: update state + cache now, persist to server.
   const commit = useCallback(
      (patch, serverKey, serverValue) => {
         const next = mergePrefs(prefsRef.current, patch);
         prefsRef.current = next;
         setPrefs(next);
         writeCache(next);
         persistKey(serverKey, serverValue);
      },
      [persistKey]
   );

   const setTheme = useCallback(
      (theme) => {
         applyTheme(theme);
         commit({ theme }, "theme", theme);
      },
      [commit]
   );

   const setDateFormat = useCallback((fmt) => commit({ dateFormat: fmt }, "dateFormat", fmt), [commit]);

   const setMoneyFormat = useCallback(
      (patch) => {
         const nextMoney = { ...prefsRef.current.money, ...patch };
         commit({ money: nextMoney }, "money", nextMoney);
      },
      [commit]
   );

   const saveView = useCallback(
      (pageKey, view) => {
         const nextViews = { ...prefsRef.current.savedViews, [pageKey]: view };
         commit({ savedViews: nextViews }, `savedView.${pageKey}`, view);
      },
      [commit]
   );

   const clearView = useCallback(
      (pageKey) => {
         const nextViews = { ...prefsRef.current.savedViews, [pageKey]: null };
         commit({ savedViews: nextViews }, `savedView.${pageKey}`, null);
      },
      [commit]
   );

   const getView = useCallback((pageKey) => prefs.savedViews?.[pageKey] ?? null, [prefs.savedViews]);

   const value = useMemo(
      () => ({ prefs, prefsReady, setTheme, setDateFormat, setMoneyFormat, saveView, clearView, getView }),
      [prefs, prefsReady, setTheme, setDateFormat, setMoneyFormat, saveView, clearView, getView]
   );

   return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = () => {
   const ctx = useContext(PreferencesContext);
   if (!ctx) throw new Error("usePreferences must be used within a PreferencesProvider");
   return ctx;
};

/** Returns a stable formatter: (amount, opts?) => string, using the user's money prefs. */
export const useMoneyFormat = () => {
   const { prefs } = usePreferences();
   return useCallback((amount, opts) => formatMoney(amount, prefs.money, opts), [prefs.money]);
};

/** Returns a stable formatter: (value) => string, using the user's date format. */
export const useDateFormat = () => {
   const { prefs } = usePreferences();
   return useCallback((value) => formatDate(value, prefs.dateFormat), [prefs.dateFormat]);
};
